import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { PageContainer, Spinner } from '@shared';

import { MDENTURE_PRODUCTION_CENTER_ID } from '@/config/mdenture';
import { useAuth } from '@/features/auth/useAuth';
import { useTranslations } from '@/i18n/client';
import { errorKeyOf, type ApiErrorKey } from '@/services/api/errors';
import { OrdersApi } from '@/services/api/orders-api';
import type { OrderArticlePrice } from '@/services/api/types';

import {
  articleValueOf,
  PACKAGE_CATEGORIES,
  PACKAGES,
  type PackageDef,
  type PackageId,
  type PackageVariantView,
  saveSelectedPackageSelection,
} from './new-case-selection';

import { isAbortError } from './orders-page-utils';

const HERO_INCLUDED_COUNT = 6;

function asLookupValue(value: string | undefined): string | null {
  if (!value) {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function normalizeCurrencyCode(currency: string | null | undefined): string | null {
  if (!currency) {
    return null;
  }

  const normalized = currency.trim().toUpperCase();

  if (normalized === '€' || normalized === 'EURO') {
    return 'EUR';
  }

  if (/^[A-Z]{3}$/.test(normalized)) {
    return normalized;
  }

  return null;
}

function formatArticlePrice(article: OrderArticlePrice, vatNote: string): string {
  if (typeof article.price !== 'number') {
    return '-';
  }

  const safeCurrency = normalizeCurrencyCode(article.currency);
  let amount: string;

  if (safeCurrency) {
    try {
      amount = new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: safeCurrency,
      }).format(article.price);
    } catch {
      amount = new Intl.NumberFormat('de-DE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(article.price);
    }
  } else {
    amount = new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(article.price);
  }

  return `${amount} ${vatNote}`;
}

function NewCaseHero() {
  const t = useTranslations('newCase.hero');
  return (
    <section className="bg-white border border-[var(--md-border)] rounded-2xl shadow-[var(--md-shadow-card)] py-5 px-4 sm:py-8 sm:px-10 mb-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="text-xs font-bold uppercase tracking-wider text-[var(--md-highlight)] mb-2">
          {t('eyebrow')}
        </p>
      </div>
      <h1 className="text-[1.75rem] mb-3">{t('title')}</h1>
      <p className="text-[var(--zs-muted)] text-sm mb-6">
        {t('intro')}
        <br />
        <strong>{t('note')}</strong>
        <br />
        {t('noteAppend')}
      </p>
      <div className="bg-[var(--md-primary-light)] border border-[var(--md-border)] rounded-2xl py-4 px-5">
        <strong className="block mb-2">{t('includedLabel')}</strong>
        <ul className="m-0 pl-5 text-[var(--zs-muted)] text-sm columns-1 sm:columns-2 lg:columns-3 gap-8">
          {Array.from({ length: HERO_INCLUDED_COUNT }, (_, i) => (
            <li key={i}>{t(`included${i}`)}</li>
          ))}
        </ul>
      </div>
    </section>
  );
}

interface PackageCardProps {
  pkg: PackageDef;
  articleVariants: readonly OrderArticlePrice[];
  isLoading: boolean;
  loadingLabel: string;
  selection: string;
  onSelectionChange: (value: string) => void;
  onChoose: () => void;
}

function PackageCard({
  pkg,
  articleVariants,
  isLoading,
  loadingLabel,
  selection,
  onSelectionChange,
  onChoose,
}: PackageCardProps) {
  const t = useTranslations(`newCase.packages.${pkg.id}`);
  const tCommon = useTranslations('newCase');

  const variants: PackageVariantView[] = articleVariants.map((article) => ({
    value: articleValueOf(article),
    label: article.description || article.productID,
    priceLabel: formatArticlePrice(article, tCommon('vatNote')),
    handlingTime: article.handlingTime,
  }));

  const selectValue = variants.some((variant) => variant.value === selection)
    ? selection
    : (variants[0]?.value ?? '');
  const hasVariants = variants.length > 0;

  return (
    <article className="md-package-card">
      <div className="md-package-card__head max-sm:px-4 max-sm:py-4">
        <h2>{t('name')}</h2>
        <p className="text-xs text-[var(--md-text-soft)] m-0">{t('subtitle')}</p>
      </div>

      <div className="md-package-card__body max-sm:gap-[0.85rem] max-sm:px-4 max-sm:py-4">
        <p className="text-sm text-[var(--zs-muted)] m-0">{t('desc')}</p>

        <ul className="md-package-card__features">
          {Array.from({ length: pkg.featureCount }, (_, i) => (
            <li key={i}>
              <span>
                <strong>{t(`feature${i}title`)}</strong>
                <small>{t(`feature${i}desc`)}</small>
              </span>
            </li>
          ))}
        </ul>

        <p className="md-package-card__ideal">
          <strong>{tCommon('suitableFor')}</strong> {t('ideal')}
        </p>

        <div className="md-package-card__price-box">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center gap-2 py-6" aria-live="polite">
              <Spinner />
              <p className="text-xs text-[var(--zs-muted)] m-0">{loadingLabel}</p>
            </div>
          ) : (
            <>
              <div className="md-package-card__price-options">
                {hasVariants ? (
                  variants.map((variant) => (
                    <div key={variant.value} className="md-package-card__price-option">
                      <span>{variant.label}</span>
                      <strong>{variant.priceLabel}</strong>
                    </div>
                  ))
                ) : (
                  <div className="md-package-card__price-option">
                    <span>{tCommon('noArticleVariants')}</span>
                    <strong>-</strong>
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-1">
                <label htmlFor={`variant-${pkg.id}`} className="zs-field__label">
                  {tCommon('selectVariant')}
                </label>
                <select
                  id={`variant-${pkg.id}`}
                  value={selectValue}
                  onChange={(e) => onSelectionChange(e.target.value)}
                  className="zs-field__input"
                  disabled={!hasVariants}
                >
                  {hasVariants ? (
                    variants.map((variant) => (
                      <option key={variant.value} value={variant.value}>
                        {variant.label}
                      </option>
                    ))
                  ) : (
                    <option value="">{tCommon('noArticleVariants')}</option>
                  )}
                </select>
              </div>
            </>
          )}
        </div>

        <div>
          <button
            type="button"
            className="zs-button max-sm:w-full"
            onClick={onChoose}
            disabled={isLoading || !hasVariants}
          >
            {tCommon('choosePackage')}
          </button>
          <p className="text-xs text-[var(--zs-muted)] mt-2.5 mb-0">{tCommon('nextStepNote')}</p>
        </div>
      </div>
    </article>
  );
}

export function NewCasePage() {
  const t = useTranslations('newCase');
  const tStatus = useTranslations('common.status');
  const tErrors = useTranslations('errors.api');
  const navigate = useNavigate();
  const { client, user } = useAuth();
  const companyId = asLookupValue(user?.orgID ?? undefined);

  const [articles, setArticles] = useState<readonly OrderArticlePrice[]>([]);
  const [hasLoadedArticleData, setHasLoadedArticleData] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorKey, setErrorKey] = useState<ApiErrorKey | null>(null);

  const [selections, setSelections] = useState<Record<PackageId, string>>({
    mill: 'bimaxillaer',
    basic: 'bimaxillaer',
    pro: 'bimaxillaer',
  });

  useEffect(() => {
    if (!client) {
      return;
    }

    if (!companyId) {
      return;
    }

    const abort = new AbortController();
    const api = new OrdersApi(client);

    queueMicrotask(() => {
      setIsLoading(true);
      setErrorKey(null);
    });

    const requestArgs: {
      articleCategories: readonly string[];
      productionCenterId?: string;
      companyId?: string;
    } = {
      articleCategories: PACKAGE_CATEGORIES,
      productionCenterId: MDENTURE_PRODUCTION_CENTER_ID,
      companyId,
    };

    void api
      .getArticles(requestArgs, abort.signal)
      .then((data) => {
        setArticles(data);
        setHasLoadedArticleData(true);

        setSelections((current) => {
          const next = { ...current };

          for (const pkg of PACKAGES) {
            const packageVariants = data.filter((article) => article.category === pkg.articleCategory);
            if (packageVariants.length === 0) {
              next[pkg.id] = '';
              continue;
            }

            const selectedValue = current[pkg.id];
            const validSelection = packageVariants.some(
              (article) => articleValueOf(article) === selectedValue,
            );

            if (!validSelection) {
              const firstVariant = packageVariants[0];
              if (firstVariant) {
                next[pkg.id] = articleValueOf(firstVariant);
              }
            }
          }

          return next;
        });
      })
      .catch((error) => {
        if (isAbortError(error)) {
          return;
        }
        setHasLoadedArticleData(false);
        setErrorKey(errorKeyOf(error));
      })
      .finally(() => {
        setIsLoading(false);
      });

    return () => {
      abort.abort();
    };
  }, [client, companyId]);

  const showCardLoading = Boolean(client && companyId) && !hasLoadedArticleData && !errorKey;

  function variantsOf(pkg: PackageDef): readonly OrderArticlePrice[] {
    return articles.filter((article) => article.category === pkg.articleCategory);
  }

  function handleChoose(pkgId: PackageId): void {
    const pkg = PACKAGES.find((entry) => entry.id === pkgId);
    if (!pkg) {
      return;
    }

    const packageVariants = variantsOf(pkg).map((article) => ({
      value: articleValueOf(article),
      label: article.description || article.productID,
      priceLabel: formatArticlePrice(article, t('vatNote')),
      handlingTime: article.handlingTime,
    }));

    if (packageVariants.length === 0) {
      return;
    }

    const selectedVariantValue = packageVariants.some((variant) => variant.value === selections[pkgId])
      ? selections[pkgId]
      : packageVariants[0]?.value;

    if (!selectedVariantValue) {
      return;
    }

    const selection = {
      packageId: pkgId,
      selectedVariantValue,
      variants: packageVariants,
    };

    saveSelectedPackageSelection(selection);
    navigate('/cases/new/details', { state: selection });
  }

  return (
    <PageContainer className="md-page--wide md-new-case-page max-sm:w-full max-sm:px-0">
      <NewCaseHero />

      {isLoading ? <p className="text-sm text-[var(--zs-muted)] mb-4">{tStatus('loading')}</p> : null}
      {errorKey ? <p className="text-sm text-[var(--md-danger)] mb-4">{tErrors(errorKey)}</p> : null}

      <section className="grid grid-cols-1 items-stretch gap-6 mb-8 sm:grid-cols-2 xl:grid-cols-3 max-sm:gap-4">
        {PACKAGES.map((pkg) => (
          <PackageCard
            key={pkg.id}
            pkg={pkg}
            articleVariants={variantsOf(pkg)}
            isLoading={showCardLoading}
            loadingLabel={tStatus('loading')}
            selection={selections[pkg.id]}
            onSelectionChange={(value) => setSelections((prev) => ({ ...prev, [pkg.id]: value }))}
            onChoose={() => handleChoose(pkg.id)}
          />
        ))}
      </section>

      <aside className="py-4 px-5 bg-[var(--md-primary-light)] border border-[var(--md-border)] rounded-2xl text-sm text-[var(--zs-muted)] mb-6">
        <strong>{t('footerNoteLabel')}</strong> {t('footerNote')}
      </aside>
    </PageContainer>
  );
}
